import { ChangeDetectionStrategy, Component, computed, Inject, inject, Optional, Signal } from '@angular/core';
import { FormlyModule } from '@ngx-formly/core';
import { NgClass } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatInputModule } from '@angular/material/input';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { TableComponent } from '../table.component';
import { ModalService } from '../../services/modal.service';
import { TitleService } from '../../services/title.service';
import type { Entry } from '../../../features/admin/modules/modules.model';
import { ModulesStore } from '../../../features/admin/modules/modules.store';
import { UtilityService } from '../../services/utility.service';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { EntriesService } from '../../../features/admin/modules/features/entries/entries.service';
import { distinctUntilChanged, merge, Observable, of, Subject, switchMap } from 'rxjs';
import { Types } from '../../interfaces/table.models';
import { cloneDeep } from 'lodash-es';
import { UserPreferenceStore } from '../../../core/services/user-preference/user-preference.store';
import { MatTooltip } from '@angular/material/tooltip';
import { UserStore } from 'app/core/services/user.store';
import { FormHelper } from '../../../features/admin/modules/features/form-builder/form.util';
import { OwnViewFieldType } from './readonly/own-view-field.type';

class DialogData {}

@Component({
  selector: 'formly-required-items',
  template: `
    <h3 class="mb-2"><mat-icon class="icon-size-3.5" svgIcon="feather:link-2"></mat-icon> &nbsp;{{ props.label }}</h3>
    <div class="mb-8 border p-4 rounded-md">
      <!-- showbulkActions is off: this field binds none of the table's bulk outputs
           (selectionChange / bulkActionClick / bulkEdit), so a checkbox column and
           the bulk bar would only add a dead Verwijderen button.
           searchTerm is the empty string, not a property: this field renders no search box at
           all. It used to pass an undeclared search field, i.e. undefined, into a required
           string input. The table reads searchTerm only for its "Geen zoekresultaten" empty
           state and a scroll reset, so there was never filtering here to lose. -->
      <app-table
        class="requirement-table w-full mb-4 flex flex-col"
        [list]="linkedEntries()"
        [searchTerm]="''"
        [showbulkActions]="false"
        [tableId]="linkedModule()?.id"
        [moduleId]="linkedModule()?.id"
        [defaultColumns]="columns()"
        [readOnly]="!canMutate()"
        (input)="updateEntry($event)">
        <ng-template #actionsTemplate let-columnInformation="column" let-entry="entry">
          <span class="min-w-32">
            @if (canMutate()) {
              <button
                mat-icon-button
                class="text-accent hover:text-primary transition-colors"
                [matTooltip]="'Bewerken'"
                type="button"
                (click)="showModal(entry)">
                <mat-icon [ngClass]="'text-inherit'">edit</mat-icon>
              </button>
            }
          </span>
        </ng-template>
      </app-table>
    </div>
  `,
  styles: `
    ::ng-deep .requirement-table section {
      gap: 0 !important;
    }

    ::ng-deep .requirement-table .min-w-64 {
      min-width: 128px !important;
    }
  `,
  imports: [
    FormlyModule,
    MatButtonModule,
    MatIconModule,
    MatExpansionModule,
    MatInputModule,
    ReactiveFormsModule,
    TableComponent,
    FormsModule,
    NgClass,
    MatTooltip,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormlyRequiredItemsComponent extends OwnViewFieldType {
  modulesStore = inject(ModulesStore);
  userPreferenceStore = inject(UserPreferenceStore);
  utilityService = inject(UtilityService);
  entriesService = inject(EntriesService);
  modalService = inject(ModalService);
  titleService = inject(TitleService);
  userStore = inject(UserStore);

  route = inject(ActivatedRoute);
  paramsParent;
  params = toSignal(this.route.paramMap);
  tableIdsuffix = 'required-items';

  currentModule = computed(() => {
    if (this.paramsParent === undefined) {
      // MAT_DIALOG_DATA is @Optional(): this field also renders outside a dialog (no
      // route.parent AND no dialog data — e.g. a Storybook shadow), where `data` is null.
      return this.data?.['entity'];
    }

    return this.modulesStore.enabledEntities().find(module => module.slug === this.paramsParent()?.get('name'));
  });

  linkedEntryId = computed(() => {
    if (this.data != undefined && this.data['entry']?.id) {
      return this.data['entry']?.id;
    } else {
      return this.params()?.get('id');
    }
  });

  currentEntry = computed(() => {
    if (this.params()?.get('id') === undefined) {
      return this.data?.['entry'] as Entry;
    }

    return this.modulesStore.entries()[this.currentModule()?.id]?.find(entry => {
      return entry.id === this.linkedEntryId();
    });
  });

  linkedModule = computed(() => {
    return this.modulesStore.enabledEntities().find(module => module.code === this.field.props.linkedModule);
  });

  private updateEntriesTrigger = new Subject<Entry>();

  fetchEntries: Observable<Entry[]> = merge(this.updateEntriesTrigger, toObservable(this.currentEntry)).pipe(
    takeUntilDestroyed(),
    distinctUntilChanged(),
    switchMap(() => {
      if (this.linkedEntryId() == null) {
        return of([]);
      }
      let filter: string = `${UtilityService.dotPathToObjectPath(this.props.linkedField)}['id'] eq '${this.linkedEntryId()}'`;
      if (this.linkedModule().code === 'shifts') {
        filter = `array_contains(entry.orders.orders, {"order": {"id": "${this.linkedEntryId()}"}}, true)`;
      }

      return this.entriesService.getEntries({
        moduleId: this.linkedModule().id,
        offset: 0,
        limit: 1000,
        filter: filter,
      });

      // let entriesIds = this.currentEntry()
      //   ?.linkedEntries?.filter(linkedEntries => linkedEntries.moduleId === this.linkedModule()?.id)
      //   .map(linkedEntries => {
      //     return linkedEntries.entryId;
      //   });
      // if (entriesIds === undefined) {
      //   return of([]) as Observable<Entry[]>;
      // }
      // entriesIds = entriesIds.flat();
      //
      // const requests = entriesIds.map(entryId => {
      //   return this.entriesService.getEntry(this.linkedModule().id, entryId) as Observable<Entry>;
      // });

      // return forkJoin(requests) as Observable<Entry[]>;
    })
  );

  type = computed(() => {
    return this.linkedModule().code === 'shifts' ? 'role' : 'category';
  });

  keyPathToArray = computed(() => {
    return this.linkedModule().code === 'shifts' ? 'entry.orders.orders' : 'entry.assets.assets';
  });

  keyPathSubject = computed(() => {
    return this.linkedModule().code === 'shifts' ? 'entry.general.users' : 'entry.assets.assets.asset';
  });

  linkedEntries: Signal<Entry[]> = toSignal(this.fetchEntries);
  filterColumnIds = ['general.date.startDate', 'general.date.endDate'];

  columns = computed(() => {
    if (this.linkedModule()?.id === undefined) {
      return [];
    }
    return this.userPreferenceStore
      .columns()
      [this.linkedModule()?.id].filter(columm => {
        return !this.filterColumnIds.includes(columm.id);
      })
      .map(column => {
        const columnCopy = JSON.parse(JSON.stringify(column));

        if (this.keyPathSubject() === column.id) {
          // columnCopy.type = Types.input;
          const field = cloneDeep(
            FormHelper.getFields(this.modulesStore.generatedForms[this.linkedModule().id]()).find(field => {
              return field.props.keyPath === this.keyPathSubject();
            })
          );
          field.key = field.props.keyPath;
          field.props.label = '';
          // columnCopy.field = field;
        }
        return columnCopy;
      });
  });

  constructor(@Optional() @Inject(MAT_DIALOG_DATA) public data: DialogData) {
    super();

    if (this.route?.parent?.paramMap) {
      this.paramsParent = toSignal(this.route?.parent?.paramMap);
    }
  }

  prefillLinkedFormValues(entry: Entry | undefined): Entry {
    if (entry) {
      return entry;
    }

    let newEntry: Entry = {
      id: undefined,
      entry: {
        archived: false,
      },
    };

    FormHelper.getFields(this.modulesStore.generatedForms()[this.linkedModule()?.id]).map((field: any) => {
      this.linkedEntryId();
      let currentEntry;
      if (this.currentEntry() != undefined) {
        currentEntry = JSON.parse(JSON.stringify(this.currentEntry()));
        delete currentEntry._etag;
      }

      if (field.props.keyPath === this.props.linkedField) {
        newEntry = UtilityService.setKeyValuePath(newEntry, field.props.keyPath, currentEntry);
      }
    });
    return newEntry;
  }

  // `canMutate()` (shared on `OwnViewFieldType`, `readonly/own-view-field.type.ts`) is this
  // class's read mode gate.
  //
  // This type is an OWN-VIEW field that is NOT in `READONLY_KEEP_ENABLED_TYPES`, so read mode
  // stamps `props.disabled` (and `props.readonly`) on it. Both of the affordances this field
  // draws mutate immediately: the edit button opens the linked entry in a modal that saves, and
  // an inline cell edit writes straight to the API through `updateEntry`. Neither has a read-only
  // mode of its own — `openEntryInModal(..., save)` still renders an editable form when `save` is
  // false, it only changes where the result goes — so read mode drops the affordance instead of
  // greying it out. The read ROW for own-view types is task 21243; until then a plain,
  // non-mutating table is the honest read face.
  showModal(entry: Entry | undefined) {
    if (!this.canMutate()) {
      return;
    }

    this.modalService.openEntryInModal(
      this.linkedModule(),
      this.prefillLinkedFormValues(entry),
      this.props.label,
      true
    );
  }

  updateEntry(entry: Entry) {
    // Guarded as well as hidden: `(input)` is the native DOM event bubbling out of any inline
    // cell editor in the table, so the write must be refused at the source and not only by the
    // absent button.
    if (!this.canMutate()) {
      return;
    }

    this.entriesService.editEntry(this.linkedModule()?.id, entry.id, entry).subscribe((entry: Entry) => {
      this.updateEntriesTrigger.next(entry);
    });
  }

  protected readonly Types = Types;
}
